import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Power, X, Layers } from "lucide-react";

const KIND_LABEL = { work: "Work", away: "Away", leave: "Leave", bank_holiday: "Bank Holiday" };

export default function CategoriesPage() {
  const [rows, setRows] = useState([]);
  const [combos, setCombos] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", kind: "work", color: "#2563EB" });
  const [comboDialog, setComboDialog] = useState({ open: false, edit: null });

  const load = async () => {
    const [{ data: cats }, { data: cmb }] = await Promise.all([
      api.get("/categories"),
      api.get("/combos").catch(() => ({ data: [] })),
    ]);
    setRows(cats); setCombos(cmb);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEdit(null); setForm({ code: "", name: "", kind: "work", color: "#2563EB" }); setOpen(true); };
  const openEdit = (c) => { setEdit(c); setForm({ code: c.code, name: c.name, kind: c.kind, color: c.color }); setOpen(true); };

  // TP-AT.RS.8.3 (Feb 2026) -- managers used to have to invent a
  // technical `code` (unique) themselves. That was too developer-y
  // for the New category modal. We now slug the display name
  // client-side and retry with a numeric suffix on the rare
  // collision so the backend `code` constraint stays intact
  // without any schema or migration change. The Code field is
  // hidden in the modal but the backend contract is unchanged.
  const _slugify = (name) => String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || "cat";

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) {
        await api.patch(`/categories/${edit.category_id}`,
          { name: form.name, kind: form.kind, color: form.color });
        toast.success("Category updated");
      } else {
        // Auto-generate a unique code from the name, retrying on
        // collision. Cap the retries so a runaway loop can never
        // hang the modal.
        const base = _slugify(form.name);
        let attempt = 0;
        let saved = false;
        while (!saved && attempt < 20) {
          const code = attempt === 0 ? base : `${base}_${attempt + 1}`;
          try {
            await api.post("/categories", { ...form, code });
            saved = true;
          } catch (err) {
            const detail = err?.response?.data?.detail || "";
            if (/already exists/i.test(String(detail))) {
              attempt += 1;
              continue;
            }
            throw err;
          }
        }
        if (!saved) {
          throw new Error("Could not generate a unique code from that name. Try a different name.");
        }
        toast.success("Category created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/categories/${c.category_id}`, { is_active: !c.is_active }); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="eyebrow">Configuration</div>
          <h1 className="font-display text-4xl mt-1">Assignment categories</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">
            How time is tracked across your roster. Categories drive utilisation reporting.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-category-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
          <Plus className="h-4 w-4 mr-1"/>New category
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {["work", "away", "leave", "bank_holiday"].map(kind => (
          <Card key={kind} title={KIND_LABEL[kind]}>
            <ul className="space-y-2">
              {rows.filter(r => r.kind === kind).map(c => (
                <li key={c.category_id}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border ${c.is_active ? "bg-white border-slate-200" : "bg-slate-50 border-slate-100 opacity-60"}`}>
                  <button onClick={()=>openEdit(c)} className="flex items-center gap-3 text-left flex-1">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: c.color }} />
                    <div>
                      <div className="text-sm font-medium">{c.name}</div>
                      <div className="text-[11px] text-[#4B5773]">{c.code}{c.is_seed ? " · seed" : ""}</div>
                    </div>
                  </button>
                  <Button size="sm" variant="ghost" onClick={()=>toggle(c)} data-testid={`cat-toggle-${c.code}`}>
                    <Power className={`h-4 w-4 ${c.is_active ? "text-[#2563EB]" : "text-slate-400"}`}/>
                  </Button>
                </li>
              ))}
              {!rows.filter(r => r.kind === kind).length && <li className="text-xs text-[#4B5773]">None.</li>}
            </ul>
          </Card>
        ))}
      </div>

      {/* Combo Assignments - templates that split one allocation across multiple work categories */}
      <div className="mt-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="eyebrow">Configuration</div>
            <h2 className="font-display text-2xl mt-1">Combo assignments</h2>
            <p className="text-sm text-[#4B5773] mt-1 max-w-2xl">
              Templates that split one allocation block across 2 to 5 work categories by percentage.
              Example: <span className="font-medium">Outbound + Inbound</span> · 70% Outbound / 30% Inbound.
              When chosen in the daily editor, the time is automatically split into the underlying categories so reports stay accurate.
            </p>
          </div>
          <Button onClick={()=>setComboDialog({ open: true, edit: null })} data-testid="add-combo-btn"
            className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
            <Plus className="h-4 w-4 mr-1"/>New combo
          </Button>
        </div>

        <Card>
          <ul className="divide-y divide-slate-100">
            {combos.map(c => (
              <li key={c.combo_id}
                className={`flex items-center justify-between py-3 ${c.is_active ? "" : "opacity-50"}`}
                data-testid={`combo-row-${c.combo_id}`}>
                <button onClick={()=>setComboDialog({ open: true, edit: c })} className="flex items-center gap-3 text-left flex-1">
                  <Layers className="h-4 w-4 text-[#2563EB]"/>
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {c.name}
                      <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#DBE7FE] text-[#1D4ED8]">Combo</span>
                    </div>
                    <div className="text-xs text-[#4B5773] mt-0.5">
                      {(c.components || []).map(cmp => {
                        const cat = rows.find(r => r.category_id === cmp.category_id);
                        return `${cmp.percentage}% ${cat?.name || "-"}`;
                      }).join(" · ")}
                    </div>
                  </div>
                </button>
                <Button size="sm" variant="ghost"
                  onClick={async () => {
                    try { await api.patch(`/combos/${c.combo_id}`, { is_active: !c.is_active }); load(); }
                    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                  }}
                  data-testid={`combo-toggle-${c.combo_id}`}>
                  <Power className={`h-4 w-4 ${c.is_active ? "text-[#2563EB]" : "text-slate-400"}`}/>
                </Button>
              </li>
            ))}
            {!combos.length && <li className="py-4 text-center text-xs text-[#4B5773]">No combos yet. Create one to split blended work like 70% Outbound / 30% Inbound.</li>}
          </ul>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{edit ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            {/* TP-AT.RS.8.3 -- Code (unique) hidden from the manager
                facing modal. It is auto-generated from the name at
                submit time. The tiny caption still displays the
                current code inside the row list so managers who
                need it for imports / reports can see it there. */}
            <div>
              <Label className="eyebrow">Name</Label>
              <Input required value={form.name} data-testid="cat-name-input"
                onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2 h-11 rounded-lg"/>
            </div>
            <div>
              <Label className="eyebrow">Kind</Label>
              <Select value={form.kind} onValueChange={(v)=>setForm({...form, kind: v})}>
                <SelectTrigger data-testid="cat-kind-select" className="mt-2 h-11 rounded-lg"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="work">Work</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                  <SelectItem value="leave">Leave</SelectItem>
                  <SelectItem value="bank_holiday">Bank Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Colour</Label>
              <Input type="color" value={form.color} data-testid="cat-color-input"
                onChange={(e)=>setForm({...form, color: e.target.value})} className="mt-2 h-11 w-32 rounded-lg"/>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" data-testid="cat-cancel-btn" onClick={()=>setOpen(false)}
                className="rounded-lg border-slate-200">Close</Button>
              <Button type="submit" data-testid="cat-submit-btn" className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                {edit ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ComboDialog open={comboDialog.open} editing={comboDialog.edit}
        workCats={rows.filter(r => r.kind === "work" && r.is_active)}
        onClose={()=>setComboDialog({ open: false, edit: null })}
        onSaved={load}/>
    </div>
  );
}

function ComboDialog({ open, editing, workCats, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#2563EB");
  const [components, setComponents] = useState([{ category_id: "", percentage: 50 }, { category_id: "", percentage: 50 }]);

  useEffect(() => {
    if (open) {
      if (editing) {
        setName(editing.name);
        setColor(editing.color || "#2563EB");
        setComponents((editing.components || []).map(c => ({ ...c, percentage: Number(c.percentage) })));
      } else {
        setName(""); setColor("#2563EB");
        setComponents([{ category_id: "", percentage: 50 }, { category_id: "", percentage: 50 }]);
      }
    }
  }, [open, editing]);

  const total = components.reduce((s, c) => s + Number(c.percentage || 0), 0);
  const totalsOk = Math.abs(total - 100) < 0.01;

  const updateComp = (i, field, v) => {
    const next = [...components];
    next[i] = { ...next[i], [field]: field === "percentage" ? Number(v) : v };
    setComponents(next);
  };
  const addComp = () => {
    if (components.length >= 5) { toast.error("A combo can have at most 5 components."); return; }
    setComponents([...components, { category_id: "", percentage: 0 }]);
  };
  const removeComp = (i) => {
    if (components.length <= 2) { toast.error("A combo needs at least 2 components."); return; }
    setComponents(components.filter((_, idx) => idx !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the combo a name."); return; }
    if (components.some(c => !c.category_id)) { toast.error("Pick a category for every component."); return; }
    if (!totalsOk) { toast.error(`Percentages must total 100% (currently ${total}%).`); return; }
    try {
      if (editing) {
        await api.patch(`/combos/${editing.combo_id}`, { name, color, components });
        toast.success("Combo updated");
      } else {
        await api.post("/combos", { name, color, components, is_active: true });
        toast.success("Combo created");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this combo? This cannot be undone.")) return;
    try { await api.delete(`/combos/${editing.combo_id}`); toast.success("Combo deleted"); onSaved(); onClose(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{editing ? "Edit combo" : "New combo assignment"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="eyebrow">Name</Label>
            <Input required value={name} data-testid="combo-name-input"
              onChange={(e)=>setName(e.target.value)} className="mt-2 h-11 rounded-lg"
              placeholder="e.g. Outbound + Inbound"/>
          </div>
          <div>
            <Label className="eyebrow">Components ({components.length} of 5 max · {total}%)</Label>
            <div className="mt-2 space-y-2">
              {components.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-slate-200 rounded-md p-2">
                  <Select value={c.category_id} onValueChange={(v)=>updateComp(i, "category_id", v)}>
                    <SelectTrigger data-testid={`combo-comp-cat-${i}`} className="flex-1 h-9 rounded-md border-slate-200">
                      <SelectValue placeholder="Pick a category"/>
                    </SelectTrigger>
                    <SelectContent>
                      {workCats.map(w => <SelectItem key={w.category_id} value={w.category_id}>{w.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" min="1" max="100" step="1" value={c.percentage}
                    data-testid={`combo-comp-pct-${i}`}
                    onChange={(e)=>updateComp(i, "percentage", e.target.value)}
                    className="h-9 w-20 rounded-md"/>
                  <span className="text-xs text-[#4B5773]">%</span>
                  {components.length > 2 && (
                    <Button type="button" variant="ghost" size="sm" data-testid={`combo-comp-remove-${i}`}
                      onClick={()=>removeComp(i)}><X className="h-4 w-4"/></Button>
                  )}
                </div>
              ))}
              {components.length < 5 && (
                <Button type="button" variant="outline" data-testid="combo-add-comp"
                  onClick={addComp} className="w-full h-9 rounded-md border-dashed">
                  <Plus className="h-4 w-4 mr-1"/>Add component
                </Button>
              )}
            </div>
            <p className={`text-xs mt-2 ${totalsOk ? "text-emerald-700" : "text-amber-700"}`} data-testid="combo-pct-total">
              {totalsOk ? "All good - totals 100%." : `Currently ${total}% - must total exactly 100%.`}
            </p>
          </div>
          <div>
            <Label className="eyebrow">Colour</Label>
            <Input type="color" value={color} data-testid="combo-color-input"
              onChange={(e)=>setColor(e.target.value)} className="mt-2 h-11 w-32 rounded-lg"/>
          </div>
          <DialogFooter>
            {editing && (
              <Button type="button" variant="ghost" data-testid="combo-delete-btn" onClick={del}
                className="text-red-600 hover:text-red-700 hover:bg-red-50">Delete</Button>
            )}
            <Button type="button" variant="outline" data-testid="combo-cancel-btn" onClick={onClose}
              className="rounded-lg border-slate-200">Close</Button>
            <Button type="submit" data-testid="combo-submit-btn" disabled={!totalsOk}
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50">
              {editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
